"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStripePaymentRequest = exports.StripePaymentRequestProvider = void 0;
const React = require("react");
const react_1 = require("react");
const StripePromiseProvider_1 = require("../StripePromiseProvider");
const useClient_1 = require("../../../../../hooks/useClient");
const SalesOrderContext_1 = require("../../../../../features/base/ordering/contexts/SalesOrderContext");
const StripePaymentNativeOptions_1 = require("./StripePaymentNativeOptions");
const StripePaymentRequestContext = (0, react_1.createContext)({
    confirmPayment: async () => ({ Success: false }),
    supportedMethods: { ApplePay: false, GooglePay: false },
    confirming: false
});
const StripePaymentRequestProvider = ({ children, onSuccessfulPayment, validateOrder }) => {
    //SPG-COMMON Contexts
    const { totalPrice } = (0, react_1.useContext)(SalesOrderContext_1.SalesOrderContext);
    const totalOrderPriceCents = Math.round(totalPrice * 100);
    const { Connected, ActiveMethods, ShowNativePayment } = (0, StripePaymentNativeOptions_1.useStripeNativeOptions)();
    const client = (0, useClient_1.useClient)();
    const retailCustomerId = (0, react_1.useMemo)(() => client.auth.session &&
        client.auth.session.retailCustomer &&
        client.auth.session.retailCustomer.id, [client.auth.session]);
    //Stripe Contexts
    const [confirming, setConfirming] = (0, react_1.useState)(false);
    const [validatedIntent, setValidatedIntent] = (0, react_1.useState)();
    const [paymentRequest, setPaymentRequest] = (0, react_1.useState)();
    const [supportedMethods, setSupportedMethods] = (0, react_1.useState)({
        GooglePay: false,
        ApplePay: false
    });
    const { StripePlatform, StripeStore, StripeStoreAccountId } = (0, StripePromiseProvider_1.useStripePromise)();
    const loadPaymentRequests = async () => {
        var _a, _b, _c, _d;
        console.log("Loading Stripe Payments");
        if (!StripePlatform || !StripeStore)
            return;
        if (Connected) {
            const foundMethods = {
                ApplePay: (_a = ActiveMethods === null || ActiveMethods === void 0 ? void 0 : ActiveMethods.includes("applePay")) !== null && _a !== void 0 ? _a : false,
                GooglePay: (_b = ActiveMethods === null || ActiveMethods === void 0 ? void 0 : ActiveMethods.includes("googlePay")) !== null && _b !== void 0 ? _b : false
            };
            setSupportedMethods(foundMethods);
        }
        else {
            let paymentReq = StripeStore.paymentRequest({
                country: "AU",
                currency: "aud",
                total: {
                    amount: totalOrderPriceCents,
                    label: "Zambrero"
                }
            });
            //check if we're on a native device, inject the supported native methods
            //check what methods the current device supports
            let foundPaymentMethods = await paymentReq.canMakePayment();
            if (foundPaymentMethods) {
                const foundMethods = {
                    ApplePay: (_c = foundPaymentMethods === null || foundPaymentMethods === void 0 ? void 0 : foundPaymentMethods.applePay) !== null && _c !== void 0 ? _c : false,
                    GooglePay: (_d = foundPaymentMethods === null || foundPaymentMethods === void 0 ? void 0 : foundPaymentMethods.googlePay) !== null && _d !== void 0 ? _d : false
                };
                setSupportedMethods(foundMethods);
                setPaymentRequest(paymentReq);
                console.log("Payment Requests Supported", foundMethods);
            }
        }
    };
    const confirmPayment = async (options) => {
        const { paymentMethodId, usePaymentRequest } = options;
        if (!StripeStore)
            return;
        setConfirming(true);
        let myResult = { Success: true };
        try {
            //if its a known credit card
            if (paymentMethodId && !usePaymentRequest) {
                //validate the order server-side
                const paymentValidation = await validateOrder({
                    currentIntent: validatedIntent
                });
                if (!(paymentValidation === null || paymentValidation === void 0 ? void 0 : paymentValidation.clientSecret))
                    throw new Error("Unable to Validate Order");
                setValidatedIntent(paymentValidation);
                //clone card to stripe store account
                const result = await client.retailCustomers.preparePaymentMethod(retailCustomerId, paymentMethodId, StripeStoreAccountId);
                //confirm the stripe payment
                const { paymentIntent, error } = await StripeStore.confirmCardPayment(paymentValidation.clientSecret, {
                    payment_method: result.storePaymentMethodId
                });
                if (error) {
                    myResult = { Success: false, Error: error.message };
                }
                else {
                    onSuccessfulPayment(paymentIntent.id);
                }
            }
            //if we're using apple pay or google pay
            if (usePaymentRequest && paymentRequest) {
                paymentRequest.on("paymentmethod", async (event) => {
                    try {
                        const paymentValidation = await validateOrder({
                            currentIntent: validatedIntent
                        });
                        if (!(paymentValidation === null || paymentValidation === void 0 ? void 0 : paymentValidation.clientSecret))
                            throw new Error("Unable to Validate Order");
                        setValidatedIntent(paymentValidation);
                        const { paymentIntent, error } = await StripeStore.confirmCardPayment(paymentValidation.clientSecret, {
                            payment_method: event.paymentMethod.id
                        });
                        if (error) {
                            event.complete("fail");
                            console.log(error.message);
                            myResult = { Success: false, Error: error.message };
                        }
                        else {
                            event.complete("success");
                            onSuccessfulPayment(paymentIntent.id);
                        }
                    }
                    catch (e) {
                        event.complete("fail");
                        myResult = { Success: false, Error: "Failed to process payment" };
                    }
                });
                paymentRequest.show();
            }
            if (usePaymentRequest && Connected) {
                let orderTotal = totalOrderPriceCents !== null && totalOrderPriceCents !== void 0 ? totalOrderPriceCents : 0;
                let nativeResult = await ShowNativePayment(orderTotal);
                if (nativeResult.Success && nativeResult.PaymentMethodId) {
                    const paymentValidation = await validateOrder({
                        currentIntent: validatedIntent
                    });
                    if (!(paymentValidation === null || paymentValidation === void 0 ? void 0 : paymentValidation.clientSecret))
                        throw new Error("Unable to Validate Order");
                    setValidatedIntent(paymentValidation);
                    const { paymentIntent, error } = await StripeStore.confirmCardPayment(paymentValidation.clientSecret, {
                        payment_method: nativeResult.PaymentMethodId
                    });
                    if (error) {
                        console.log(error);
                    }
                    else {
                        onSuccessfulPayment(paymentIntent.id);
                    }
                }
            }
        }
        catch (e) {
            if (e instanceof Error) {
                myResult = { Success: false, Error: e.message };
            }
            else {
                myResult = { Success: false, Error: "Unexpected Error Occurred." };
            }
            console.log("Confirm Payment Error", e);
        }
        setConfirming(false);
        return myResult;
    };
    (0, react_1.useEffect)(() => {
        loadPaymentRequests();
    }, [StripePlatform, StripeStore, Connected]);
    const overview = {
        totalPriceCents: totalOrderPriceCents
    };
    const myContext = {
        confirmPayment: confirmPayment,
        supportedMethods: supportedMethods,
        overview: overview,
        confirming: confirming
    };
    return (React.createElement(StripePaymentRequestContext.Provider, { value: myContext }, children));
};
exports.StripePaymentRequestProvider = StripePaymentRequestProvider;
const useStripePaymentRequest = () => {
    const context = (0, react_1.useContext)(StripePaymentRequestContext);
    if (context === undefined) {
        throw new Error("useStripePaymentRequest must be used within a StripePaymentRequestProvider");
    }
    return context;
};
exports.useStripePaymentRequest = useStripePaymentRequest;
//# sourceMappingURL=StripePaymentRequestProvider.js.map